import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { FaUserPlus, FaTimes } from "react-icons/fa";
import {
  collection,
  doc,
  getCountFromServer,
  getFirestore,
  serverTimestamp,
  setDoc
} from "../utility/firestoreGateway";
import { toast } from "react-toastify";
import { useFirebase } from "../context/FirebaseContext";
import { useI18n } from "../i18n/useI18n";
import {
  getFriendCountCacheKey,
  invalidateFriendRosterCache
} from "../utility/friendRosterCache";
import {
  buildFriendDocFromRequest,
  FRIEND_REQUEST_STATUS,
  getFriendRequestDisplayName
} from "../utility/friendRequests";
import { invalidateCachedValue, writeCachedValue } from "../utility/localDataCache";
import { markNotificationsLogStale } from "../utility/activityFeedFreshness";
import { getUserProfileCacheKey } from "../utility/profileCache";
import { isDemoUserId } from "../utility/demoSession";
import defaultPhoto from "../images/default-photo.jpeg";
import type firebase from "firebase/compat/app";
import type { FriendRequest } from "../utility/friendRequests";
import { isTimestamp } from "../types/data";
interface PendingFriendRequest extends FriendRequest { id: string; ref: firebase.firestore.DocumentReference; requesterId: string; }
const requestCreatedAt = (request: PendingFriendRequest) => {
  const createdAt = request.createdAt;
  return isTimestamp(createdAt) ? createdAt.toMillis?.() || 0 : createdAt instanceof Date ? createdAt.getTime() : 0;
};

const sortFriendRequests = (items: PendingFriendRequest[]) =>
  [...items].sort((left, right) => {
    const leftTime = requestCreatedAt(left);
    const rightTime = requestCreatedAt(right);
    return leftTime - rightTime || String(left.requesterId).localeCompare(String(right.requesterId));
  });

function FriendRequestModal() {
  const { auth, firestore } = useFirebase();
  const [user] = useAuthState(auth);
  const { t } = useI18n();
  const modularDb = useMemo(() => getFirestore(), []);
  const [requests, setRequests] = useState<PendingFriendRequest[]>([]);
  const [processing, setProcessing] = useState("");
  const activeRequest = requests[0] || null;

  useEffect(() => {
    if (!user?.uid || !firestore || isDemoUserId(user.uid)) {
      setRequests([]);
      return undefined;
    }

    const unsubscribe = firestore
      .collection("users")
      .doc(user.uid)
      .collection("friendRequests")
      .where("status", "==", FRIEND_REQUEST_STATUS.pending)
      .onSnapshot(
        (snapshot) => {
          const items = snapshot.docs.map((requestDoc): PendingFriendRequest => ({
            id: requestDoc.id,
            ref: requestDoc.ref,
            ...(requestDoc.data() as Omit<PendingFriendRequest, "id" | "ref">)
          }));
          setRequests(sortFriendRequests(items));
        },
        (error) => {
          console.warn("Не удалось загрузить входящие запросы в друзья:", error);
        }
      );

    return unsubscribe;
  }, [firestore, user?.uid]);

  const refreshFriendCountSummary = useCallback(async (ownerId: string) => {
    if (!ownerId) return;
    try {
      const countSnapshot = await getCountFromServer(
        collection(modularDb, "users", ownerId, "friends")
      );
      const friendsCount = Math.max(0, Number(countSnapshot.data().count || 0));
      await setDoc(doc(modularDb, "users", ownerId), {
        publicStats: {
          friendsCount,
          updatedAt: serverTimestamp()
        }
      }, { merge: true });
      writeCachedValue(getFriendCountCacheKey(ownerId), friendsCount);
      invalidateCachedValue(getUserProfileCacheKey(ownerId));
    } catch (error) {
      console.warn("Не удалось обновить счётчик друзей после запроса:", error);
    }
  }, [modularDb]);

  const markRequest = useCallback(async (status: "accepted" | "kept_as_follower") => {
    if (!activeRequest?.ref || !user?.uid || processing) return;
    setProcessing(status);

    try {
      if (status === FRIEND_REQUEST_STATUS.accepted) {
        const friendDoc = buildFriendDocFromRequest(activeRequest, new Date());
        const friendRef = firestore
          .collection("users")
          .doc(user.uid)
          .collection("friends")
          .doc(activeRequest.requesterId);

        await friendRef.set(friendDoc, { merge: true });
        await Promise.all([
          invalidateCachedValue(`friend_relation_${user.uid}_${activeRequest.requesterId}`),
          invalidateCachedValue(`friend_relation_${activeRequest.requesterId}_${user.uid}`),
          invalidateFriendRosterCache(user.uid),
          invalidateFriendRosterCache(activeRequest.requesterId)
        ]);
        await refreshFriendCountSummary(user.uid);
      }

      await activeRequest.ref.set({
        status,
        respondedAt: new Date(),
        updatedAt: new Date()
      }, { merge: true });
      markNotificationsLogStale(user.uid, "owner");

      toast.success(
        status === FRIEND_REQUEST_STATUS.accepted
          ? t("Вы теперь друзья")
          : t("Пользователь оставлен в подписчиках"),
        {
          position: "top-right",
          autoClose: 2200
        }
      );
    } catch (error) {
      console.error("Не удалось обработать запрос в друзья:", error);
      toast.error(t("Не удалось обработать запрос в друзья"), {
        position: "top-right",
        autoClose: 2600
      });
    } finally {
      setProcessing("");
    }
  }, [activeRequest, firestore, processing, refreshFriendCountSummary, t, user?.uid]);

  if (isDemoUserId(user?.uid) || !activeRequest) return null;

  const requesterName = getFriendRequestDisplayName(activeRequest);
  const avatarUrl = activeRequest.requesterAvatarUrl || activeRequest.requesterPhotoURL || defaultPhoto;
  const acceptBusy = processing === FRIEND_REQUEST_STATUS.accepted;
  const keepBusy = processing === FRIEND_REQUEST_STATUS.keptAsFollower;
  const disabled = Boolean(processing);

  return createPortal(
    <div className="friend-request-modal" role="presentation">
      <section
        className="friend-request-modal__card"
        role="dialog"
        aria-modal="true"
        aria-label={t("Вас добавили в друзья")}
      >
        <div className="friend-request-modal__icon" aria-hidden="true">
          <FaUserPlus />
        </div>
        <button
          className="friend-request-modal__close"
          type="button"
          onClick={() => markRequest(FRIEND_REQUEST_STATUS.keptAsFollower)}
          disabled={disabled}
          aria-label={t("Оставить в подписчиках")}
        >
          <FaTimes />
        </button>
        <img
          className="friend-request-modal__avatar"
          src={avatarUrl}
          alt=""
          loading="lazy"
          decoding="async"
        />
        <p className="friend-request-modal__eyebrow">{t("Новый подписчик")}</p>
        <h2>{t("Вас добавили в друзья")}</h2>
        <p className="friend-request-modal__text">
          <strong>{requesterName}</strong> {t("добавил вас в друзья.")}
        </p>
        <div className="friend-request-modal__actions">
          <button
            className="friend-request-modal__button friend-request-modal__button--secondary"
            type="button"
            onClick={() => markRequest(FRIEND_REQUEST_STATUS.keptAsFollower)}
            disabled={disabled}
          >
            {keepBusy ? t("Сохраняем...") : t("Оставить в подписчиках")}
          </button>
          <button
            className="friend-request-modal__button friend-request-modal__button--primary"
            type="button"
            onClick={() => markRequest(FRIEND_REQUEST_STATUS.accepted)}
            disabled={disabled}
            autoFocus
          >
            {acceptBusy ? t("Добавляем...") : t("Добавить его в друзья")}
          </button>
        </div>
      </section>
    </div>,
    document.body
  );
}

export default FriendRequestModal;
